<template>
  <main-logo-register />

  <FormCard
    :hideImg="!isAdmin"
    :image-src="content.src"
    :title="!isAdmin ? content.title :''"
    :description="content.description"
    :card-style="{ maxWidth: '700px', paddingLeft: '50px', paddingRight: '50px' }"
    :class="{ admin: isAdmin }"
  >
    <div class="content">
      <div>
        <template v-if="isAdmin">
          <h3>
            Estás a un solo paso de volver a navegar en <br/>tu ambiente.
          </h3>
          <div style="margin-top: 20px; text-align: center;">
            Según la fecha de inicio, se cobrará el total o un prorrateo del plan.
          </div>
          <table aria-hidden="true">
            <tr>
              <td>Inicio de contrato</td>
              <td>{{ formatDate(String(planConfig?.contract_start_at)) }}</td>
            </tr>
            <tr>
              <td>Nombre del plan</td>
              <td>{{ planConfig?.plan?.name }}</td>
            </tr>
            <tr>
              <td>Número de slots</td>
              <td>{{ planConfig?.payment_detail?.slots_per_month }}</td>
            </tr>
            <tr>
              <td class="price_text">
                Precio por slot
                <a-tooltip>
                  <template #title>
                    <span v-if="planConfig?.payment_detail?.is_prorated == 'True'">
                      Este precio corresponde a un prorrateo del precio por slot.
                    </span>
                    <span v-else>
                      Este precio corresponde al total del precio por slot.
                    </span>
                  </template>
                  <InfoCircleOutlined />
                </a-tooltip>
              </td>
              <td>${{ Number(planConfig?.payment_detail?.price_per_slot) }}</td>
            </tr>
            <tr v-if="planConfig?.payment_detail?.tax > 0">
              <td>
                IGV ({{planConfig?.payment_detail?.tax * 100}})% de slots
                <a-tooltip>
                  <template #title>
                    Cálculo del ({{planConfig?.payment_detail?.tax * 100}}%) de IGV sobre el precio por slot multiplicado por el número de slots. 
                    Es un costo adicional separado.
                  </template>
                  <InfoCircleOutlined />
                </a-tooltip>
              </td>
              <td>${{ Number(planConfig?.payment_detail?.total_tax_price) }}</td>
            </tr>
            <tr>
              <td><strong>Total a pagar:</strong></td>
              <td>
                <strong>${{ Number(planConfig?.payment_detail?.total_price) }}</strong>
              </td>
            </tr>
          </table>

          <PayUPaymentButton
            name="Realizar el pago"
            :amount="Number(planConfig?.payment_detail?.total_price)"
            :type="ProductType.Recharge"
            @update="$emit('update')"
            @success="handleOpenModal('success')"
            @reject="handleOpenModal('error')"
          />

          <TransactionStateModal
            v-model:open="transactionModal.open"
            :state="transactionModal.state"
          />

          <span class="text-info">
            Si tienes alguna duda, puedes escribirnos a support@beexcc.com
          </span>
        </template>

        <template v-else>
          <img :src="billingLockImage" alt="lock" />
        </template>
      </div>
    </div>
  </FormCard>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { computed, onMounted, reactive, ref } from "vue";
import dayjs, { type Dayjs } from "dayjs";

import { useValidateWorkspace } from "@/app/auth/composables/useValidateWorkspace";

import FormCard from "@/app/auth/components/atoms/FormCard.vue";
import MainLogoRegister from "@/app/shared/components/molecules/MainLogoRegister.vue";
import billingLockImage from "@/app/settings/assets/billing-lock.png";
import PayUPaymentButton from "@/app/shared/components/buttons/PayUPaymentButton.vue";
import { InfoCircleOutlined } from "@ant-design/icons-vue";
import { ProductType } from "@/@types/settings/billing";

/**
 * Query params
 *
 * slot: string;
 * admin: "True" | "False";
 * date_payment: string;
 * date_payment_finish: string;
 * price: string;
 * slots_invoice: string;
 */

const route = useRoute();
const router = useRouter();
const workspaceData = useValidateWorkspace();
const planConfig = ref<any>(null);

const formatDate = (date: string | Dayjs) => dayjs(date).format("DD/MM/YYYY");

const isAdmin = computed(() => route.query.admin);

const content = computed(() => ({
  src: isAdmin.value ? billingLockImage : workspaceData.value?.picture,
  title: isAdmin.value
    ? `Tu cuenta se ha bloqueado el ${formatDate(String(route.query.date_payment_finish))}. 😥`
    : `El equipo de trabajo de ${workspaceData.value?.name} se encuentra bloqueado temporalmente 😥 `,
  description: isAdmin.value
    ? ""
    : "Por favor, comuníquese con su administrador ante cualquier duda. ",
}));

const transactionModal = reactive({
  open: false,
  state: "success",
});

const handleOpenModal = (state: string) => {
  transactionModal.state = state;
  if (transactionModal.state === "success") {
    return;
  } else {
    transactionModal.open = true;
  }
};

onMounted(() => {
  const planConfigLS = localStorage.getItem("planConfig");
  if (planConfigLS) {
    planConfig.value = JSON.parse(planConfigLS);
    console.log("Plan Config:", planConfig.value);
    localStorage.removeItem("planConfig");
  } else {
    router.push("/login");
  }
})
</script>

<style scoped lang="sass">
.content
  display: flex
  // flex-direction: column
  align-items: center
  justify-content: center
  height: 100%
  h3
    text-align: center
    font-size: 24px
    color: $gray-9
    font-weight: 600
    margin: 0
  .text-info  
    font-size: 14px
    font-weight: 400
    color: #000000A6
    display: block
    margin-top: 12px
    text-align: center

img
  margin-top: 48px

.admin:deep(.card .caption h2)
  color: $gray-8 !important

table
  width: 70%
  margin: 30px auto

td
  width: 50%
  padding: 7px 0

tr:not(:last-child)
  border-bottom: 1px solid $text-cuaternary

.price_text
  display: flex
  align-items: center
  width: 100%
  gap: 5px
</style>
